import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { observer, useLocalStore } from 'mobx-react';
import useMobxStores from 'hooks/useMobxStores';
import { Spinner } from '@patternfly/react-core';
import PatternflyInput from 'components/JSONSchemaForm/PatternflyInput';
import PatternflySelect from 'components/JSONSchemaForm/PatternflySelect';
import { getCreateClassReturnRoute } from 'components/ClassesV2/instructorDashboard.routeFilters';
import Form from '../../JSONSchemaForm';
import DateTimeWidget from '../../JSONSchemaForm/DateTimeWidget';
import TimezoneWidget from '../../JSONSchemaForm/TimezoneWidget';
import Store from './store';
import './styles.scss';

const ObjectFieldTemplate = (props) => (
  <div>
    {props.title}
    {props.description}
    {props.properties.map((element) => element.content)}
  </div>
);

ObjectFieldTemplate.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  properties: PropTypes.array,
};

ObjectFieldTemplate.defaultProps = {
  title: '',
  description: '',
  properties: [],
};

const EditClass = (props) => {
  const { uuid } = props;

  const { t } = useTranslation();

  const { routerStore, catalogStore, uiStore, classesStore } = useMobxStores();

  const store = useLocalStore(() => new Store(catalogStore, classesStore));

  useEffect(() => {
    const _populateFields = async () => {
      store.language = uiStore.currentLanguage;
      store.schema = {
        type: 'object',
        required: [
          'name',
          'vtc_account',
          'course',
          'product',
          'start_time',
          'end_time',
        ],
        properties: {
          name: {
            title: t('Title'),
            type: 'string',
            maxLength: 255,
          },
          vtc_account: {
            title: t('VTC Account'),
            type: 'string',
            enum: [],
            enumNames: [],
          },
          course: {
            title: t('Course'),
            type: 'string',
            enum: [],
            enumNames: [],
          },
          product: {
            title: t('Modality'),
            type: 'string',
            enum: ['ilt', 'vt', 'os', 'osvt', 'lr'],
            enumNames: ['ILT', 'VT', 'OS', 'OSVT', 'LR'],
          },
          delivery_responsibility: {
            title: t('Delivery Responsibility'),
            type: 'string',
            enum: ['rht', 'niit'],
            enumNames: ['RedHat', 'NIIT'],
          },
          enable_ssh: {
            title: t('Enable SSH'),
            type: 'boolean',
          },
          lab_auto_start: {
            title: t(
              'Lab Auto Start  *Disabled for manually VT or virtual lab not required',
            ),
            type: 'boolean',
            default: false,
          },
          instructor: {
            type: 'object',
            title: '',
            properties: {
              email: {
                title: t('Instructor Email'),
                type: 'string',
              },
              name: {
                title: t('Instructor Name'),
                type: 'string',
              },
              username: {
                title: t('Instructor'),
                type: 'string',
                enum: [],
                enumNames: [],
              },
            },
          },
          timezone: {
            title: t('Class Timezone'),
            type: 'string',
          },
          start_time: {
            title: t('Access Start Time'),
            type: 'string',
            format: 'date-time',
          },
          end_time: {
            title: t('Access End Time'),
            type: 'string',
            format: 'date-time',
          },
          extension_days: {
            title: t('Lab Extension Days From Access End Time '),
            type: 'integer',
            minimum: 0,
            maximum: 100,
            default: 0,
          },
          capacity: {
            title: t('Capacity'),
            type: 'integer',
            minimum: 0,
            maximum: 50,
          },
          instructor_conference_url: {
            title: t('Instructor Conference URL'),
            type: 'string',
          },
          conference_url: {
            title: t('Student Conference URL'),
            type: 'string',
          },
        },
      };

      store.uiSchema = {
        name: {
          'ui:widget': PatternflyInput,
          'ui:label': false,
        },
        vtc_account: {
          'ui:widget': PatternflySelect,
          'ui:label': false,
          'ui:options': {
            placeholder: t('Select an account'),
          },
        },
        course: {
          'ui:widget': PatternflySelect,
          'ui:label': false,
          'ui:options': {
            placeholder: t('Select a course'),
          },
        },
        product: {
          'ui:widget': PatternflySelect,
          'ui:label': false,
        },
        delivery_responsibility: {
          'ui:widget': PatternflySelect,
          'ui:label': false,
        },
        lab_auto_start: {
          'ui:disabled': true,
        },
        instructor: {
          email: {
            'ui:widget': 'hidden',
          },
          name: {
            'ui:widget': 'hidden',
          },
          username: {
            'ui:widget': PatternflySelect,
            'ui:label': false,
            'ui:options': {
              placeholder: t('Select an instructor'),
            },
          },
        },
        timezone: {
          'ui:widget': TimezoneWidget,
        },
        start_time: {
          'ui:widget': DateTimeWidget,
        },
        end_time: {
          'ui:widget': DateTimeWidget,
        },
        extension_days: {
          'ui:widget': PatternflyInput,
          'ui:label': false,
          'ui:options': {
            type: 'number',
            min: 0,
            max: 100,
          },
        },
        capacity: {
          'ui:widget': PatternflyInput,
          'ui:label': false,
          'ui:options': {
            type: 'number',
            min: 0,
            max: 50,
          },
        },
        instructor_conference_url: {
          'ui:widget': PatternflyInput,
          'ui:label': false,
          'ui:options': {
            type: 'url',
          },
        },
        conference_url: {
          'ui:widget': PatternflyInput,
          'ui:label': false,
          'ui:options': {
            type: 'url',
          },
        },
      };
      try {
        await store.populateFields(uuid);
      } catch (error) {
        console.warn(error);
      }
    };
    _populateFields();
  }, []);

  useEffect(() => {
    store.updateCourseTitles(uiStore.currentLanguage);
  }, [uiStore.currentLanguage]);

  const onSubmit = (e) => {
    const action = uuid ? store.put(uuid, e.formData) : store.post(e.formData);

    action
      .then(() => {
        if (uuid) {
          routerStore.navigate('classes:view', { uuid });
          return;
        }
        routerStore.navigate(
          getCreateClassReturnRoute(routerStore.route?.params?.from),
        );
      })
      .catch((error) => console.warn(error));
  };

  const getInstructor = (instructorUsername) => {
    if (!instructorUsername) {
      return '';
    }

    const instructor =
      store.users.find((user) => {
        const username = (instructorUsername || '').toLowerCase();
        return username === user.username;
      }) || {};
    const { first_name: firstName, last_name: lastName } = instructor;
    instructor.fullName =
      firstName || lastName ? `${firstName} ${lastName}` : '';

    return instructor;
  };

  return (
    <div className="edit-class-form">
      <Form
        schema={store.schema}
        uiSchema={store.uiSchema}
        formData={store.formData}
        onSubmit={onSubmit}
        buttonProps={{
          isDisabled: store.isSubmittedEditClass,
          isLoading: store.isSubmittedEditClass,
        }}
        onChange={(form) => {
          form.formData.instructor.name = getInstructor(
            form.formData.instructor.username,
          ).fullName;
          form.formData.instructor.email = getInstructor(
            form.formData.instructor.username,
          ).email;
          store.formData = form.formData;
        }}
        formContext={{
          timezone: store.formData.timezone,
        }}
        ObjectFieldTemplate={ObjectFieldTemplate}
      />
      {store.isSubmittedEditClass ? (
        <div className="edit-class-form__overlay">
          <div className="edit-class-form__overlay-backdrop" aria-hidden />
          <div className="edit-class-form__overlay-spinner">
            <Spinner size="xl" aria-label={t('Saving')} />
          </div>
        </div>
      ) : null}
    </div>
  );
};

EditClass.propTypes = {
  uuid: PropTypes.string,
};

EditClass.defaultProps = {
  uuid: null,
};

export default observer(EditClass);
